import type { ReactNode } from 'react';
import { red } from 'providers/theme/themePrimitives';
import { FormattedMessage, useIntl } from 'react-intl';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import BusinessRoundedIcon from '@mui/icons-material/BusinessRounded';
import DashboardRoundedIcon from '@mui/icons-material/DashboardRounded';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import AccountTreeRoundedIcon from '@mui/icons-material/AccountTreeRounded';
import { Box, Chip, Paper, alpha, Stack, useTheme, Typography, Grid2 as Grid, CircularProgress } from '@mui/material';

type ModernDashboardHeaderProps = {
  companyName: string | null;
  companyCount: number;
  productCount: number;
  contactCount: number;
  isEditing: boolean;
  isCancelled: boolean;
  loading?: boolean;
};

type SummaryItemProps = {
  icon: ReactNode;
  label: ReactNode;
  value: ReactNode;
};

const SummaryItem = ({ icon, label, value }: SummaryItemProps) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        p: 1.5,
        height: '100%',
        minWidth: 0,
        border: '1px solid',
        borderColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.26 : 0.14),
        borderRadius: 2,
        bgcolor: alpha(theme.palette.background.default, theme.palette.mode === 'dark' ? 0.56 : 0.76),
      }}>
      <Stack direction="row" spacing={1.25} alignItems="center">
        <Box
          aria-hidden="true"
          sx={{
            width: 34,
            height: 34,
            display: 'grid',
            flexShrink: 0,
            placeItems: 'center',
            color: theme.palette.mode === 'dark' ? theme.palette.primary.light : theme.palette.primary.dark,
            borderRadius: 1.5,
            bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.18 : 0.1),
            '& .MuiSvgIcon-root': { fontSize: 19 },
          }}>
          {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
            {label}
          </Typography>
          <Typography variant="subtitle2" noWrap sx={{ mt: 0.25, fontWeight: 700 }} title={String(value)}>
            {value}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
};

const ModernDashboardHeader = ({
  companyName,
  companyCount,
  productCount,
  contactCount,
  isEditing,
  isCancelled,
  loading = false,
}: ModernDashboardHeaderProps) => {
  const intl = useIntl();
  const theme = useTheme();
  const hasSelection = Boolean(companyName) && !loading;

  const status = isEditing
    ? intl.formatMessage({ id: 'dashboard.modern.status.editing' })
    : intl.formatMessage({ id: 'dashboard.modern.status.viewing' });

  return (
    <Paper
      component="header"
      elevation={0}
      sx={{
        mb: 2.5,
        p: { xs: 2, md: 3 },
        overflow: 'hidden',
        position: 'relative',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2.5,
        background:
          theme.palette.mode === 'dark'
            ? `linear-gradient(135deg, ${alpha(theme.palette.primary.dark, 0.18)} 0%, ${alpha(
                theme.palette.background.paper,
                0.96
              )} 58%)`
            : `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.2)} 0%, ${alpha(
                theme.palette.background.default,
                0.98
              )} 62%)`,
        '&::after': {
          content: '""',
          position: 'absolute',
          width: 220,
          height: 220,
          top: -130,
          right: -70,
          borderRadius: '50%',
          pointerEvents: 'none',
          bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.08 : 0.055),
        },
      }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', md: 'flex-start' }}
        sx={{ position: 'relative', zIndex: 1 }}>
        <Stack direction="row" spacing={1.75} sx={{ minWidth: 0 }}>
          <Box
            aria-hidden="true"
            sx={{
              width: 50,
              height: 50,
              display: 'grid',
              flexShrink: 0,
              placeItems: 'center',
              color: theme.palette.mode === 'dark' ? theme.palette.primary.light : theme.palette.primary.dark,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.18 : 0.11),
              '& .MuiSvgIcon-root': { fontSize: 27 },
            }}>
            <DashboardRoundedIcon />
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" flexWrap="wrap" alignItems="center" gap={1} sx={{ mb: 0.5 }}>
              <Typography component="h1" variant="h4" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                <FormattedMessage id="dashboard.modern.title" />
              </Typography>
              {loading && (
                <Chip
                  size="small"
                  role="status"
                  aria-live="polite"
                  icon={<CircularProgress size={14} color="inherit" />}
                  label={<FormattedMessage id="dashboard.modern.loadingDetails" />}
                  sx={{ borderRadius: '999px', '& .MuiChip-icon': { color: 'inherit' } }}
                />
              )}
              {hasSelection && isCancelled && (
                <Chip
                  size="small"
                  variant="outlined"
                  label={<FormattedMessage id="dashboard.modern.status.cancelled" />}
                  color="error"
                  sx={{
                    color: theme.palette.mode === 'dark' ? red[300] : red[600],
                    borderColor: theme.palette.mode === 'dark' ? red[300] : red[600],
                    borderRadius: '999px',
                    fontWeight: 600,
                  }}
                />
              )}
              {hasSelection && (
                <Chip
                  size="small"
                  variant="outlined"
                  label={status}
                  color={isEditing ? 'warning' : 'default'}
                  sx={{ borderRadius: '999px', fontWeight: 600 }}
                />
              )}
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 720 }}>
              <FormattedMessage id="dashboard.modern.subtitle" />
            </Typography>
          </Box>
        </Stack>
      </Stack>

      {hasSelection && (
        <Grid container spacing={1.5} sx={{ mt: 2.5, position: 'relative', zIndex: 1 }}>
          <Grid size={{ xs: 12, sm: 12, lg: 3 }}>
            <SummaryItem
              icon={<BusinessRoundedIcon />}
              label={<FormattedMessage id="dashboard.modern.summary.selectedProject" />}
              value={companyName}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4, lg: 3 }}>
            <SummaryItem
              icon={<AccountTreeRoundedIcon />}
              label={<FormattedMessage id="dashboard.modern.summary.companies" />}
              value={companyCount}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4, lg: 3 }}>
            <SummaryItem
              icon={<Inventory2RoundedIcon />}
              label={<FormattedMessage id="dashboard.modern.summary.products" />}
              value={productCount}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4, lg: 3 }}>
            <SummaryItem
              icon={<GroupsRoundedIcon />}
              label={<FormattedMessage id="dashboard.modern.summary.contacts" />}
              value={contactCount}
            />
          </Grid>
        </Grid>
      )}
    </Paper>
  );
};

export default ModernDashboardHeader;
